// frontend/src/features/workflows/components/CompositionForm.jsx
import React, { useState, useEffect, useRef } from 'react';

const OPTIONS = {
  phospholipidType: [
    'Lecitin', 'DPPC', 'DSPC', 'DOPC', 'Egg PC', 'Soy PC',
  ],
  cholesterolType: [
    'Cholesterol',
    'Cholesteryl oleate',
    'Cholesteryl hemisuccinate',
    'Cholesteryl sulfate',
    'Dehydrocholesterol',
  ],
  ionizableType: [
    'D-Lin-MC3-DMA', 'C12-200', 'SM-102', 'ALC-0315', '5A2-SC8',
  ],
  pegType: [
    'DMG-PEG 2000',
    'mPEG-DSPE',
    'DSG-PEG 2000',
    'mPEG Ceramides',
    'mPEG-Cholesterol',
  ],
};

export function CompositionForm({ initialValues = {}, onChange }) {
  const [vals, setVals] = useState({
    phospholipidType:  initialValues.phospholipidType || '',
    phospholipid:      initialValues.phospholipid     != null ? String(initialValues.phospholipid) : '',
    cholesterolType:   initialValues.cholesterolType  || '',
    cholesterol:       initialValues.cholesterol      != null ? String(initialValues.cholesterol) : '',
    ionizableType:     initialValues.ionizableType    || '',
    ionizable:         initialValues.ionizable        != null ? String(initialValues.ionizable) : '',
    pegType:           initialValues.pegType          || '',
    peg:               initialValues.peg              != null ? String(initialValues.peg) : '',
    mixRatio:          initialValues.mixRatio         != null ? String(initialValues.mixRatio) : '',
  });
  const [touched, setTouched] = useState(false);
  const onChangeRef = useRef(onChange);
  const lastEmittedRef = useRef(null);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Normalize and emit only when values actually change.
  useEffect(() => {
    const payload = {
      phospholipidType: vals.phospholipidType,
      phospholipid:     Number(vals.phospholipid) || 0,
      cholesterolType:  vals.cholesterolType,
      cholesterol:      Number(vals.cholesterol) || 0,
      ionizableType:    vals.ionizableType,
      ionizable:        Number(vals.ionizable) || 0,
      pegType:          vals.pegType,
      peg:              Number(vals.peg) || 0,
      // <-- preserve the raw string here
      mixRatio:         vals.mixRatio
    };
    const previous = lastEmittedRef.current;
    if (
      previous &&
      previous.phospholipidType === payload.phospholipidType &&
      previous.phospholipid === payload.phospholipid &&
      previous.cholesterolType === payload.cholesterolType &&
      previous.cholesterol === payload.cholesterol &&
      previous.ionizableType === payload.ionizableType &&
      previous.ionizable === payload.ionizable &&
      previous.pegType === payload.pegType &&
      previous.peg === payload.peg &&
      previous.mixRatio === payload.mixRatio
    ) {
      return;
    }
    lastEmittedRef.current = payload;
    onChangeRef.current?.(payload);
  }, [vals]);

  // sum of the four lipid %s
  const total = () =>
    ['phospholipid','cholesterol','ionizable','peg']
      .reduce((sum, k) => sum + (Number(vals[k]) || 0), 0);

  // handlers
  const handleTyped = key => e => {
    setVals(cur => ({ ...cur, [key]: e.target.value }));
  };
  const handleFocus = key => () => {
    if (vals[key] === '0') {
      setVals(cur => ({ ...cur, [key]: '' }));
    }
  };
  const handleBlur = key => () => {
    let v = parseInt(vals[key], 10);
    if (isNaN(v)) v = 0;
    v = Math.max(0, Math.min(100, v));

    // enforce 4-lipid sum ≤100
    if (['phospholipid','cholesterol','ionizable','peg'].includes(key)) {
      const otherSum = ['phospholipid','cholesterol','ionizable','peg']
        .filter(k => k !== key)
        .reduce((s, k) => s + (Number(vals[k]) || 0), 0);
      v = Math.min(v, Math.max(0, 100 - otherSum));
    }

    setVals(cur => ({ ...cur, [key]: String(v) }));
    setTouched(true);
  };

  const renderRow = (label, typeKey, pctKey) => (
    <div className="flex items-center space-x-4 mb-4">
      <div className="w-32 font-medium">{label}</div>

      <select
        value={vals[typeKey]}
        onChange={e => { handleTyped(typeKey)(e); setTouched(true); }}
        className="flex-1 p-2 border rounded"
      >
        <option value="" disabled>— select type —</option>
        {OPTIONS[typeKey].map(opt => (
          <option key={opt} value={opt}>{opt}</option>
        ))}
      </select>

      <div className="flex items-center">
        <input
          type="text"
          placeholder="%"
          value={vals[pctKey]}
          onFocus={handleFocus(pctKey)}
          onChange={handleTyped(pctKey)}
          onBlur={handleBlur(pctKey)}
          className="w-20 p-2 border rounded text-right"
        />
        <span className="ml-1">%</span>
      </div>
    </div>
  );

  return (
    <div>
      {renderRow('Phospholipid', 'phospholipidType', 'phospholipid')}
      {renderRow('Cholesterol',  'cholesterolType',  'cholesterol')}
      {renderRow('Ionizable',    'ionizableType',    'ionizable')}
      {renderRow('PEG',          'pegType',          'peg')}

      {touched && total() !== 100 && (
        <p className="mb-4 text-red-600 font-medium">
          Total lipid %: {total()}% (must equal 100%)
        </p>
      )}

      {/* Mix Ratio as a volume ratio (e.g. 3:2) */}
      <div className="flex items-center space-x-4 mb-4">
        <div className="w-32 font-medium">Mix Ratio</div>
        <input
          type="text"
          placeholder="e.g. 3:2"
          value={vals.mixRatio}
          onChange={handleTyped('mixRatio')}
          className="flex-1 p-2 border rounded"
        />
      </div>
    </div>
  );
}
